import { call, put, select } from 'redux-saga/effects';
import applicationSpinnerActions from 'redux/actions/applicationSpinnerActions';
import {
  calculateDistanceEngineSelector,
  lengthUnitSelector,
  selectMoveVersion,
  selectTenantId,
  showConfirmationFollowUpPageSelector,
} from 'redux/selectors/config';
import { getSynergySessionToken } from 'utils/sagas-utils';
import {
  caseGuidCreatedSelector,
  currentLocationCoordinatesSelector,
} from 'redux/selectors/caseCreation';
import { sendSelectedDestinationApi } from 'api/sendSelectedDestination';
import { convertMilesToKm, convertKmToMiles } from 'utils/global-utils';
import {
  categorySelector,
  destinationChoicesSelector,
  destinationCoordinatesSelector,
  serviceIdSelector,
  destinationCurrentLocationSelector,
} from 'redux/selectors/destination';
import {
  parseCoordinatesToBing,
  isBingMapCoordinate,
} from 'containers/Location/components/LocationWithBing/useLocationWithBing';
import { getDistance } from 'api/getDistance';
import type {
  GoogleMapCoordinates,
  ChoiceResponse,
  GetDistanceResponse,
  SendSelectedDestinationResponse,
  ChoicesMapped,
  TemporalLocation,
  BingMapCoordinates,
} from 'ts/types';
import { ApiResponse } from 'ts/interfaces/api-response-getDistance.interface';
import { ChoicesMapping, UnitDistance } from 'constants/synergy';
import destinationActions from 'redux/actions/destinationActions';
import { routes } from 'Routes/routesConfig';
import { push } from 'connected-react-router';
import type { sendSelectedDestinationActionType } from 'redux/actions/destinationAction.types';

const { toggleDestinationAlert, sendSelectedDestination } = destinationActions;

export function formatDestinationAddress(destinationAddress: TemporalLocation) {
  const {
    address: {
      countryRegion,
      countryRegionIso2,
      locality,
      postalCode,
      addressLine,
      adminDistrict,
    },
    coordinates,
  } = destinationAddress;

  const formattedCoordinates = parseCoordinatesToBing(coordinates);

  return {
    address: {
      locality,
      street_address: addressLine,
      postal_code: postalCode,
      state: countryRegion,
      country: countryRegionIso2,
      subdivision: adminDistrict,
    },
    coordinates: formattedCoordinates,
  };
}

function* getLocationData(choiceId: string) {
  if (!choiceId) {
    return false;
  }
  const [choiceFiltered] = ChoicesMapping.filter(
    choice => choice.id === choiceId,
  ) as ChoicesMapped[];

  if (!choiceFiltered.id) {
    return false;
  }

  let destinationAddress: TemporalLocation;
  let destinationAddressFormatted = {};

  const shouldSendLocation = !!choiceFiltered.must_select_location;

  if (shouldSendLocation) {
    destinationAddress = yield select(destinationCurrentLocationSelector);
    destinationAddressFormatted = formatDestinationAddress(destinationAddress);
  }
  return destinationAddressFormatted;
}

function* handleResponse(status: number) {
  const shouldRedirectToConfirmationFollowUp: boolean = yield select(
    showConfirmationFollowUpPageSelector,
  );

  if (status === 204) {
    yield put(
      shouldRedirectToConfirmationFollowUp
        ? push(routes.SYNERGY_FOLLOW_CONFIRMATION)
        : push(routes.CONFIRMATION),
    );
  } else if (status === 202) {
    yield put(push(routes.CALLBACK));
  } else {
    yield put(push(routes.CONTACT_US));
  }

  return false;
}

export function* sendSelectedDestinationSaga(
  action: sendSelectedDestinationActionType,
) {
  const { startLoad, stopLoad } = applicationSpinnerActions;
  const choiceId = action.payload;
  try {
    yield put(startLoad({}));

    const tenantId: string = yield select(selectTenantId);
    const moveVersion: string = yield select(selectMoveVersion);
    const sessionToken: string = yield getSynergySessionToken();
    const serviceId: string = yield select(serviceIdSelector);
    const caseGuid: string = yield select(caseGuidCreatedSelector);
    const locationData: object = yield getLocationData(choiceId);

    const response: ApiResponse<SendSelectedDestinationResponse> = yield call(
      sendSelectedDestinationApi,
      {
        body: { destination: { category: choiceId, ...locationData } },
        caseGuid,
        serviceId,
        token: sessionToken,
        tenantId,
        moveVersion,
      },
    );
    const { status } = response;

    yield handleResponse(status);
    return;
  } catch {
  } finally {
    yield put(stopLoad({}));
  }
}

function* showMaxDistanceAlert(
  isMaximumDistanceUnitMiles: boolean,
  maxDistanceValue: number,
  allowedDistance: number,
) {
  let allowedDistanceAux;

  const lengthUnit: string = yield select(lengthUnitSelector);

  const isLengthUnitShownMiles = (lengthUnit as string) === 'mi';

  if (isMaximumDistanceUnitMiles && isLengthUnitShownMiles) {
    allowedDistanceAux = maxDistanceValue;
  } else if (!isMaximumDistanceUnitMiles && isLengthUnitShownMiles) {
    allowedDistanceAux = convertKmToMiles(allowedDistance as number);
  } else {
    allowedDistanceAux = allowedDistance;
  }

  const destinationAlertDistanceValue = ` ${allowedDistanceAux}`;

  const destinationAlertDistanceUnitToTranslate =
    `${lengthUnit as string}` === UnitDistance.MILES
      ? 'length_unit_miles'
      : 'length_unit_km';

  yield put(
    toggleDestinationAlert({
      destinationAlertDistanceValue,
      destinationAlertDistanceUnitToTranslate,
      showDestinationModal: true,
    }),
  );
}

export function selectChoice(
  categorySelected: string,
  choices: ChoiceResponse[],
) {
  let choiceSelected;
  if (categorySelected && choices) {
    choiceSelected = choices.find(
      choice => choice.category === categorySelected,
    );
  }
  return choiceSelected;
}

export function* getDestinationDistanceSaga() {
  let destinationCoordFormatted: BingMapCoordinates = {},
    locationCoordFormatted: BingMapCoordinates = {};
  const categorySelected: string = yield select(categorySelector);
  const destinationCoords: BingMapCoordinates | GoogleMapCoordinates =
    yield select(destinationCoordinatesSelector);
  const currentLocationCoord: BingMapCoordinates | GoogleMapCoordinates =
    yield select(currentLocationCoordinatesSelector);
  const choices: ChoiceResponse[] = yield select(destinationChoicesSelector);
  const choiceSelected: ChoiceResponse = yield selectChoice(
    categorySelected,
    choices,
  );
  const engine: string = yield select(calculateDistanceEngineSelector);

  if (choiceSelected) {
    const {
      maximum_distance: { unit = '', value: maxDistanceValue = 0 } = {},
    } = choiceSelected;

    const isMaximumDistanceUnitMiles = unit === 'Mile' || unit === '[MI_I]';

    const allowedDistance = isMaximumDistanceUnitMiles
      ? convertMilesToKm(maxDistanceValue as number)
      : maxDistanceValue;

    destinationCoordFormatted = yield parseCoordinatesToBing(destinationCoords);
    locationCoordFormatted = yield parseCoordinatesToBing(currentLocationCoord);
    if (isBingMapCoordinate(destinationCoordFormatted)) {
      const body = {
        originCoordinates: { ...locationCoordFormatted },
        destinationCoordinates: { ...destinationCoordFormatted },
        engine,
      };
      const response: ApiResponse<GetDistanceResponse> = yield call(
        getDistance,
        body,
      );

      if (response?.result?.travelDistance <= allowedDistance) {
        yield put(sendSelectedDestination(categorySelected));
      } else {
        yield showMaxDistanceAlert(
          isMaximumDistanceUnitMiles,
          maxDistanceValue,
          allowedDistance as number,
        );
      }
    }
  }
}
